/* Fawem Shopify şablonu — tema JS (bağımlılık: gsap, ScrollTrigger, lenis → assets/ içinde vendor edilmiş)
 *
 * Mimari: genel davranışlar `document` üzerinde olay delegasyonuyla çalışır (dinamik içerikte yeniden kurulum gerekmez).
 * Öğe başına kurulum gereken şeyler (reveal, word-scrub, sayaç, yığılan kartlar, galeri…) initSection(root) içinde;
 * tema editöründe bir bölüm yeniden çizilince (`shopify:section:load`) sadece o bölüm için tekrar çalışır.
 * Hareket katmanı koşulsuzdur (stüdyo kuralı) — motion-baseline.md.
 */
(() => {
  'use strict';

  const T = window.theme || { routes: {}, settings: {}, strings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const raf = (fn) => requestAnimationFrame(fn);

  // Hafif rAF tween — sayaç/preloader için GSAP yüklemeye gerek kalmasın
  const easeOutExpo = (p) => (p === 1 ? 1 : 1 - Math.pow(2, -10 * p));
  const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);
  function tween(from, to, ms, ease, onUpdate, onDone) {
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / ms);
      onUpdate(from + (to - from) * ease(p));
      if (p < 1) raf(step); else if (onDone) onDone();
    };
    raf(step);
  }

  /* ─────────────────────────── Lenis (yumuşak kaydırma) ─────────────────────────── */
  let lenis = null;
  const hasGsap = () => typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

  // GSAP + ScrollTrigger yalnızca gereken bölüm sayfadaysa yüklenir (yığılan kartlar, data-gsap'li özel sahneler).
  // Ürün/koleksiyon/sepet sayfaları ~115 KB JS ve sürekli ticker maliyetinden kurtulur.
  let gsapPromise = null;
  function loadGsap() {
    if (hasGsap()) return Promise.resolve();
    if (gsapPromise) return gsapPromise;
    const load = (src) => new Promise((res, rej) => { const s = document.createElement('script'); s.src = src; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
    gsapPromise = load(T.assets.gsap).then(() => load(T.assets.scrollTrigger)).then(() => {
      window.gsap.registerPlugin(window.ScrollTrigger);
      if (lenis) lenis.on('scroll', window.ScrollTrigger.update);
    });
    return gsapPromise;
  }

  function initLenis() {
    if (!T.settings.smoothScroll || typeof window.Lenis === 'undefined') return;
    // Dokunmatik cihazda Lenis parmak kaydırmayı yumuşatmaz (syncTouch kapalı), sadece rAF maliyeti getirir.
    // Programatik kaydırma (back-to-top, # linkleri) orada native smooth scroll ile yapılır.
    if (window.matchMedia('(pointer: coarse)').matches) return;
    // respectReducedMotion: false — Lenis ≥1.3 varsayılanı true; iPhone'da smooth scroll'u sessizce öldürür.
    lenis = new window.Lenis({ respectReducedMotion: false, duration: 1.1, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    window.lenis = lenis;
    // Tek sürücü: Lenis kendi rAF döngüsüyle döner; GSAP sonradan yüklenirse sadece ScrollTrigger.update bağlanır.
    const loop = (time) => { lenis.raf(time); raf(loop); };
    raf(loop);
    // sayfa içi # linkleri
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -90, duration: 1.1 });
    });
  }
  const onScroll = (fn) => {
    if (lenis) lenis.on('scroll', fn);
    window.addEventListener('scroll', fn, { passive: true });
  };

  /* ─────────────────────────── Preloader ─────────────────────────── */
  function initPreloader() {
    const el = $('#preloader');
    if (!el || !T.settings.preloader) return;
    const num = $('#preloader-percent');
    const bar = $('#preloader-bar');
    const state = { v: 0 };
    const paint = () => { const n = Math.round(state.v); if (num) num.textContent = n; if (bar) bar.style.width = n + '%'; };
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      const end = () => el.classList.add('is-hidden');
      tween(state.v, 100, 400, easeOutCubic, (v) => { state.v = v; paint(); }, end);
    };
    tween(0, 92, 1400, easeOutCubic, (v) => { if (!done) { state.v = v; paint(); } });
    if (document.readyState === 'complete') setTimeout(finish, 900);
    else window.addEventListener('load', () => setTimeout(finish, 600));
    setTimeout(finish, 3500); // güvenlik ağı: sayfayı asla engelleme
  }

  /* ─────────────────────────── Reveal (staggered) ─────────────────────────── */
  let revealIO = null;
  let revealPending = [];
  function observeReveals(root = document) {
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); } });
      }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });
    }
    $$('.reveal, .reveal-stagger', root).forEach((el) => {
      if (el.dataset.rv || el.classList.contains('is-in')) return;
      el.dataset.rv = '1';
      revealPending.push(el);
      revealIO.observe(el);
    });
    revealCatchUp();
  }
  // hızlı/programatik scroll IO'yu atlatırsa öğe görünmez kalmasın
  function revealCatchUp() {
    revealPending = revealPending.filter((el) => {
      if (!el.isConnected || el.classList.contains('is-in')) return false;
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) { el.classList.add('is-in'); revealIO.unobserve(el); return false; }
      return true;
    });
  }

  /* ─────────────────────────── word-scrub (okuma efekti) ─────────────────────────── */
  const WS_INLINE = { A: 1, STRONG: 1, EM: 1, B: 1, I: 1, SPAN: 1, U: 1, SMALL: 1, MARK: 1 };

  function wsWrap(node) {
    for (let i = node.childNodes.length - 1; i >= 0; i--) {
      const c = node.childNodes[i];
      if (c.nodeType === 3) {
        if (!/\S/.test(c.nodeValue)) continue;
        const frag = document.createDocumentFragment();
        c.nodeValue.split(/(\s+)/).forEach((part) => {
          if (part === '') return;
          if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part));
          else { const s = document.createElement('span'); s.className = 'word-scrub'; s.textContent = part; frag.appendChild(s); }
        });
        node.replaceChild(frag, c);
      } else if (c.nodeType === 1 && WS_INLINE[c.tagName] && !c.querySelector('img,svg,picture,video')) {
        wsWrap(c);
      }
    }
  }
  function wsEligible(el) {
    if (el.closest('form, figcaption, nav, header, footer, button, summary, [data-noscrub], .product-card, .accordion__body, .drawer')) return false;
    if (/\beyebrow\b|uppercase|badge/.test(el.className)) return false;
    if (el.querySelector('img, svg, picture, video, ul, ol, div, h1, h2, h3, h4')) return false;
    return (el.textContent || '').trim().split(/\s+/).length >= 4;
  }
  // Kelime başına IntersectionObserver: kaydırma sırasında getBoundingClientRect (zorunlu yerleşim) yok.
  // Eski hâli her karede tüm bekleyen kelimeleri ölçüyordu → orta telefonda ~25 fps (Tiryakioğlu, 2026-10-08).
  let wsIO = null;
  function wsObserve(words) {
    if (!wsIO) {
      wsIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting || e.boundingClientRect.top < 0) { e.target.classList.add('is-visible'); wsIO.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -32% 0px' });
    }
    words.forEach((w) => wsIO.observe(w));
  }
  function initWordScrub(root = document) {
    if (!T.settings.wordScrub) return;
    // Kelime bölme DOM'u epey değiştirir — ilk boyamayı/LCP'yi bekletmesin diye boşta çalıştır.
    const run = () => {
      $$('main .word-scrub-target, main p, main li', root).forEach((el) => {
        if (el.dataset.wsDone || !wsEligible(el)) return;
        el.dataset.wsDone = '1';
        wsWrap(el);
      });
      wsObserve($$('.word-scrub:not(.is-visible)', root));
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1500 });
    else setTimeout(run, 300);
  }

  /* ─────────────────────────── Sayaç roll-up ─────────────────────────── */
  function initCounters(root = document) {
    $$('[data-counter]', root).forEach((el) => {
      if (el.dataset.cDone) return;
      el.dataset.cDone = '1';
      const raw = el.dataset.counter || '';
      const to = parseFloat(raw.replace(/[^\d.]/g, ''));
      if (!isFinite(to)) return;
      const suffix = raw.replace(/[\d.,\s]/g, '');
      el.textContent = '0' + suffix;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.disconnect();
          tween(0, to, 1400, easeOutExpo, (v) => { el.textContent = Math.round(v).toLocaleString('tr-TR') + suffix; });
        });
      }, { threshold: 0.6 });
      io.observe(el);
    });
  }

  /* ─────────────────────────── Yığılan kartlar (signature) ─────────────────────────── */
  const stackCtx = new WeakMap();
  function initStackCards(root = document) {
    const wraps = $$('[data-stack-cards]', root);
    if (!wraps.length) return;
    loadGsap().then(() => {
     wraps.forEach((wrap) => {
      if (stackCtx.has(wrap)) stackCtx.get(wrap).revert();
      const cards = $$('.stack-card', wrap);
      const ctx = window.gsap.context(() => {
        cards.forEach((card, i) => {
          if (i === cards.length - 1) return;
          window.gsap.to(card, { scale: 0.94, filter: 'brightness(0.78)', ease: 'none', scrollTrigger: { trigger: cards[i + 1], start: 'top bottom', end: 'top 25%', scrub: true } });
        });
      }, wrap);
      stackCtx.set(wrap, ctx);
     });
     window.ScrollTrigger.refresh();
    }).catch(() => { /* GSAP yüklenemezse kartlar yine sticky olarak çalışır */ });
  }
  function destroyStackCards(root) {
    $$('[data-stack-cards]', root).forEach((wrap) => { if (stackCtx.has(wrap)) { stackCtx.get(wrap).revert(); stackCtx.delete(wrap); } });
  }

  /* ─────────────────────────── Duyuru çubuğu ─────────────────────────── */
  const announceTimers = new WeakMap();
  function initAnnouncement(root = document) {
    $$('[data-announcement]', root).forEach((bar) => {
      clearInterval(announceTimers.get(bar));
      const msgs = $$('[data-announcement-msg]', bar);
      if (msgs.length < 2) return;
      let i = 0;
      announceTimers.set(bar, setInterval(() => {
        msgs[i].classList.add('hidden');
        i = (i + 1) % msgs.length;
        msgs[i].classList.remove('hidden');
      }, 4500));
    });
  }

  /* ─────────────────────────── Yukarı çık çentiği ─────────────────────────── */
  function initBackToTop() {
    const btn = $('#backToTop');
    if (!btn) return;
    let shown = null;
    const sync = () => { const v = (window.scrollY || 0) > 620; if (v !== shown) { shown = v; btn.classList.toggle('show', v); } };
    btn.addEventListener('click', () => {
      if (lenis) lenis.scrollTo(0, { duration: 1.0 });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    onScroll(sync);
    sync();
  }

  /* ─────────────────────────── Çekmeceler ─────────────────────────── */
  let lastOpener = null;
  function openDrawer(id, opener) {
    const d = document.getElementById(id);
    if (!d) return;
    lastOpener = opener || document.activeElement;
    d.classList.add('is-open');
    $$('[data-drawer-open="' + id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    document.documentElement.style.overflow = 'hidden';
    if (lenis) lenis.stop();
    const first = $('button, a[href], input, select, textarea', $('.drawer__panel', d));
    setTimeout(() => first && first.focus({ preventScroll: true }), 80);
  }
  function closeDrawer(d) {
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    $$('[data-drawer-open="' + d.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (!$('.drawer.is-open')) {
      document.documentElement.style.overflow = '';
      if (lenis) lenis.start();
    }
    if (lastOpener && lastOpener.focus) lastOpener.focus({ preventScroll: true });
  }
  function initDrawers() {
    document.addEventListener('click', (e) => {
      const open = e.target.closest('[data-drawer-open]');
      if (open) { e.preventDefault(); openDrawer(open.dataset.drawerOpen, open); return; }
      if (e.target.closest('[data-drawer-close]')) closeDrawer(e.target.closest('.drawer'));
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer($('.drawer.is-open'));
      if (e.key === 'Tab') { // basit odak tuzağı
        const d = $('.drawer.is-open');
        if (!d) return;
        const f = $$('button:not([disabled]), a[href], input:not([disabled]), select, textarea, summary', d).filter((x) => x.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ─────────────────────────── Sepet ─────────────────────────── */
  const jsonFetch = async (url, body) => {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || T.strings.error);
    return data;
  };

  function setCartCount(n) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = n; el.classList.toggle('hidden', !n); });
    $$('[data-cart-count-label]').forEach((el) => { el.textContent = '(' + n + ')'; });
  }
  function renderCartDrawer(html) {
    const drawer = $('[data-cart-drawer]');
    if (!drawer || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-drawer]', doc);
    if (fresh) drawer.innerHTML = fresh.innerHTML;
  }
  async function refreshCart() {
    const res = await fetch(T.routes.cart + '.js', { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    setCartCount(cart.item_count);
    if ($('[data-cart-drawer]')) {
      const s = await (await fetch(T.routes.root + '?sections=cart-drawer')).json();
      renderCartDrawer(s['cart-drawer']);
    }
    return cart;
  }

  async function addToCart(form) {
    const btn = $('[data-add-button]', form);
    const label = $('[data-add-label]', form);
    const err = $('[data-form-error]', form);
    if (err) { err.classList.add('hidden'); err.textContent = ''; }
    if (btn && btn.disabled) return;
    const original = label ? label.textContent : '';
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    if (label) label.textContent = T.strings.loading;
    try {
      const fd = new FormData(form);
      if (T.settings.cartType === 'drawer') fd.append('sections', 'cart-drawer');
      const res = await fetch(T.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.error);
      if (T.settings.cartType === 'drawer' && data.sections && data.sections['cart-drawer']) {
        renderCartDrawer(data.sections['cart-drawer']);
        const cart = await (await fetch(T.routes.cart + '.js')).json();
        setCartCount(cart.item_count);
        openDrawer('cart-drawer', btn);
      } else {
        const cart = await (await fetch(T.routes.cart + '.js')).json();
        setCartCount(cart.item_count);
      }
      if (label) label.textContent = T.strings.added;
      setTimeout(() => { if (label) label.textContent = original; if (btn) btn.disabled = false; }, 1600);
      if (btn) btn.classList.remove('is-loading');
      return;
    } catch (e) {
      if (err) { err.textContent = e.message; err.classList.remove('hidden'); }
      if (label) label.textContent = original;
    }
    if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
  }

  async function changeLine(line, quantity) {
    const drawer = $('[data-cart-drawer]');
    try {
      const data = await jsonFetch(T.routes.cartChange + '.js', { line, quantity, sections: 'cart-drawer' });
      setCartCount(data.item_count);
      if (data.sections && data.sections['cart-drawer']) renderCartDrawer(data.sections['cart-drawer']);
      if (!drawer) location.reload();
    } catch (e) { console.error(e); }
  }

  function initCart() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      addToCart(form);
    });
    document.addEventListener('click', (e) => {
      const q = e.target.closest('[data-cart-qty]');
      if (q) { changeLine(+q.dataset.cartQty, Math.max(0, +q.dataset.qty)); return; }
      const r = e.target.closest('[data-cart-remove]');
      if (r) { changeLine(+r.dataset.cartRemove, 0); return; }

      // sepet sayfası adet butonları
      const step = e.target.closest('[data-line-step]');
      if (step) {
        const input = document.getElementById(step.dataset.lineInput);
        if (!input) return;
        input.value = Math.max(0, (+input.value || 0) + +step.dataset.lineStep);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    document.addEventListener('change', (e) => {
      const input = e.target.closest('#cart-form input[type="number"]');
      if (!input) return;
      if (input.dataset.sync) { const twin = document.getElementById(input.dataset.sync); if (twin) twin.value = input.value; }
      const form = input.form;
      clearTimeout(form._t);
      form._t = setTimeout(() => form.requestSubmit($('[name="update"]', form)), 450);
    });
  }

  /* ─────────────────────────── Ürün sayfası ─────────────────────────── */
  function initProduct(root = document) {
    $$('[data-product-root]', root).forEach((el) => {
      if (el.dataset.pDone) return;
      el.dataset.pDone = '1';
      const jsonEl = $('[data-product-json]', el);
      const variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
      const form = $('[data-product-form]', el);
      const picker = $('[data-variant-picker]', el);
      const track = $('[data-gallery-track]', el);
      const thumbs = $$('[data-gallery-thumb]', el);

      const goTo = (i) => {
        if (!track) return;
        const slide = $$('[data-gallery-slide]', track)[i];
        if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      };
      thumbs.forEach((b) => b.addEventListener('click', () => goTo(+b.dataset.galleryThumb)));
      if (track && thumbs.length) {
        let t;
        track.addEventListener('scroll', () => {
          clearTimeout(t);
          t = setTimeout(() => {
            const i = Math.round(track.scrollLeft / track.clientWidth);
            thumbs.forEach((b, n) => b.setAttribute('aria-current', n === i ? 'true' : 'false'));
          }, 60);
        }, { passive: true });
      }

      const update = () => {
        if (!picker) return;
        const chosen = $$('input[data-option-index]:checked', picker).sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex).map((i) => i.value);
        $$('[data-option-label]', el).forEach((l) => { l.textContent = chosen[+l.dataset.optionLabel] || ''; });
        const v = variants.find((x) => x.options.every((o, i) => o === chosen[i]));
        const btn = $('[data-add-button]', el);
        const label = $('[data-add-label]', el);
        const idInput = $('[data-variant-id]', el);
        if (!v) {
          if (btn) btn.disabled = true;
          if (label) label.textContent = T.strings.unavailable;
          return;
        }
        if (idInput) { idInput.value = v.id; idInput.disabled = !v.available; }
        if (btn) btn.disabled = !v.available;
        if (label) label.textContent = v.available ? T.strings.addToCart : T.strings.soldOut;
        const price = $('[data-price]', el), cmp = $('[data-compare]', el), badge = $('[data-sale-badge]', el);
        if (price) { price.textContent = v.price; price.classList.toggle('text-accent-600', v.on_sale); }
        if (cmp) { cmp.textContent = v.on_sale ? v.compare : ''; cmp.classList.toggle('hidden', !v.on_sale); }
        if (badge) badge.classList.toggle('hidden', !v.on_sale);
        if (v.position) goTo(v.position - 1);
        const url = new URL(location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url);
      };
      if (picker) picker.addEventListener('change', update);
    });

    // adet +/-
    if (!document.body.dataset.qtyBound) {
      document.body.dataset.qtyBound = '1';
      document.addEventListener('click', (e) => {
        const s = e.target.closest('[data-qty-step]');
        if (!s) return;
        const input = $('[data-qty-input]', s.parentElement);
        if (input) input.value = Math.max(1, (+input.value || 1) + +s.dataset.qtyStep);
      });
      document.addEventListener('click', (e) => {
        const c = e.target.closest('[data-copy-link]');
        if (!c) return;
        navigator.clipboard && navigator.clipboard.writeText(c.dataset.copyLink).then(() => {
          const old = c.textContent; c.textContent = c.dataset.copied; setTimeout(() => { c.textContent = old; }, 1800);
        });
      });
    }

    // önerilen ürünler
    $$('[data-recommendations]', root).forEach(async (box) => {
      if (box.dataset.rDone || !box.dataset.url) return;
      box.dataset.rDone = '1';
      try {
        const html = await (await fetch(box.dataset.url)).text();
        const fresh = $('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
        if (fresh && fresh.innerHTML.trim()) { box.innerHTML = fresh.innerHTML; observeReveals(box); }
      } catch (e) { /* sessizce geç */ }
    });
  }

  /* ─────────────────────────── Koleksiyon / hesap küçük davranışlar ─────────────────────────── */
  function initMisc() {
    document.addEventListener('change', (e) => {
      const a = e.target.closest('[data-auto-submit]');
      if (a && a.form) a.form.requestSubmit();
    });
    // giriş: şifremi unuttum
    const login = $('#login-panel'), recover = $('#recover-panel');
    if (login && recover) {
      const toggle = (show) => { recover.classList.toggle('hidden', !show); login.classList.toggle('hidden', show); };
      document.addEventListener('click', (e) => { if (e.target.closest('[data-toggle-recover]')) { e.preventDefault(); toggle(recover.classList.contains('hidden')); } });
      if (location.hash === '#recover') toggle(true);
    }
    // adresler
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-toggle-address]');
      if (t) { const box = document.getElementById('address-edit-' + t.dataset.toggleAddress); if (box) box.classList.toggle('hidden'); return; }
      const d = e.target.closest('[data-delete-address]');
      if (d && window.confirm(d.dataset.confirm)) {
        const f = document.createElement('form');
        f.method = 'post'; f.action = d.dataset.deleteAddress;
        f.innerHTML = '<input type="hidden" name="_method" value="delete">';
        document.body.appendChild(f); f.submit();
      }
    });
    $$('select[data-default]').forEach((s) => { if (s.dataset.default) s.value = s.dataset.default; });
  }

  /* ─────────────────────────── Bölüm yaşam döngüsü ─────────────────────────── */
  function initSection(root = document) {
    observeReveals(root);
    initWordScrub(root);
    initCounters(root);
    initStackCards(root);
    initAnnouncement(root);
    initProduct(root);
    if (hasGsap()) window.ScrollTrigger.refresh();
  }

  function boot() {
    initLenis();
    initPreloader();
    initDrawers();
    initCart();
    initMisc();
    initBackToTop();
    initSection(document);
    // reveal catch-up yalnızca bekleyen öğe varken ve en fazla 250 ms'de bir (her karede konum okumasın)
    let rcLast = 0;
    onScroll(() => { if (!revealPending.length) return; const n = performance.now(); if (n - rcLast < 250) return; rcLast = n; revealCatchUp(); });
    // font/resim yüklenince yerleşim kayar → ScrollTrigger'ı tazele
    window.addEventListener('load', () => hasGsap() && window.ScrollTrigger.refresh());
  }

  // Tema editörü: yeniden çizilen bölüm için sadece o bölümü kur
  document.addEventListener('shopify:section:load', (e) => initSection(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroyStackCards(e.target));
  document.addEventListener('shopify:section:select', () => hasGsap() && window.ScrollTrigger.refresh());

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
